import React from "react";
import { Link } from "react-router-dom";
import {
  Facebook,
  Instagram,
  Twitter,
  Mail,
  Phone,
  MapPin,
  Linkedin,
} from "lucide-react";
import logoImage from "../images/logo.png";

const Footer = () => {
  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  return (
    <footer className="bg-ayur-brown text-ayur-beige" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 md:py-5">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 px-6 py-6 mt-6">
          {/* Logo */}
          <div className="lg:col-span-2">
            <Link
              to="/"
              className="flex items-center gap-2"
              data-testid="logo-link"
            >
              <img
                src={logoImage}
                alt="Fracaso Logo"
                className="h-12 w-28 object-contain"
              />
            </Link>
            <p className="text-ayur-beige/80 mb-3 max-w-sm leading-relaxed text-sm">
              Reviving ancient Ayurvedic wisdom through modern technology.
            </p>
            <div className="flex gap-3">
              <a
                href="https://www.facebook.com/fracasohealth/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#1877F2] hover:bg-[#166fe5] text-white flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/fracasohealth/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#E4405F] hover:bg-[#d33e5f] text-white flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/FracasoW"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-black hover:bg-gray-800 text-white flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
                aria-label="X"
              >
                <Twitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/company/fracaso-smart-tech/"
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-[#0A66C2] hover:bg-[#0952b4] text-white flex items-center justify-center transition-colors shadow-md hover:shadow-lg"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
            </div>
          </div>

          {/* Company Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm mb-2">Company</h4>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link
                  to="/about"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  About Us
                </Link>
              </li>
              <li>
                <Link
                  to="/ayurbot"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  Ayurbot
                </Link>
              </li>
              <li>
                <Link
                  to="/franchise"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  Franchise
                </Link>
              </li>
            </ul>
          </div>

          {/* Products Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm mb-2">
              Products
            </h4>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link
                  to="/products"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  All Products
                </Link>
              </li>
              <li>
                <Link
                  to="/products?category=herbal-tea"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  Herbal Teas
                </Link>
              </li>
            </ul>
          </div>

          {/* Support Links */}
          <div>
            <h4 className="font-heading font-semibold text-sm mb-2">Support</h4>
            <ul className="space-y-1.5 text-sm">
              <li>
                <Link
                  to="/contact"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  Contact Us
                </Link>
              </li>
              <li>
                <Link
                  to="/shipping"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  Shipping Policy
                </Link>
              </li>
              <li>
                <Link
                  to="/refunds"
                  onClick={handleScrollToTop}
                  className="text-ayur-beige/70 hover:text-ayur-gold transition-colors"
                >
                  Returns
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Contact Info */}
        <div className="mt-4 pt-4 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-center items-center gap-3 md:gap-6 mb-4 text-sm flex-wrap">
            <a
              href="mailto:info@fracaso.in"
              className="flex items-center gap-1.5 text-ayur-beige/70 hover:text-ayur-gold transition-colors"
            >
              <Mail className="h-3 w-3" />
              <span>info@fracaso.in</span>
            </a>
            <a
              href="tel:+916309962513"
              className="flex items-center gap-1.5 text-ayur-beige/70 hover:text-ayur-gold transition-colors"
            >
              <Phone className="h-3 w-3" />
              <span>+91 63099 62513</span>
            </a>
            <div className="flex items-center gap-1.5 text-ayur-beige/70">
              <MapPin className="h-3 w-3" />
              <span>Hyderabad, Telangana, India</span>
            </div>
          </div>

          {/* Copyright */}
          <div className="flex flex-col md:flex-row justify-between items-center gap-2 text-xs text-ayur-beige/60">
            <p>
              &copy; {new Date().getFullYear()} Fracaso Smart Tech. All rights
              reserved.
            </p>
            <div className="flex gap-3">
              <Link
                to="/privacy"
                onClick={handleScrollToTop}
                className="hover:text-ayur-gold transition-colors"
              >
                Privacy
              </Link>
              <Link
                to="/terms"
                onClick={handleScrollToTop}
                className="hover:text-ayur-gold transition-colors"
              >
                Terms
              </Link>
              <Link
                to="/shipping"
                onClick={handleScrollToTop}
                className="hover:text-ayur-gold transition-colors"
              >
                Shipping
              </Link>
              <Link
                to="/refunds"
                onClick={handleScrollToTop}
                className="hover:text-ayur-gold transition-colors"
              >
                Refunds
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
